/**
 * SMS broadcasts + keywords API (migrations 0116/0117).
 * Companion to api/smsLists.ts (lists/members/drip).
 */
import { apiFetch } from './client';

export type BroadcastStatus =
  | 'draft' | 'scheduled' | 'running' | 'paused' | 'completed' | 'cancelled';

export interface SmsBroadcastRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  name: string;
  body: string;
  list_id: string | null;
  from_number: string | null;
  /** 'manual' = portal-created; 'folder_notify' = automatic folder alert. */
  source: 'manual' | 'folder_notify';
  status: BroadcastStatus;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  sent_count: number;
  failed_count: number;
  skipped_dnc_count: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  /** Live aggregates over this broadcast's sms_messages rows. */
  live_total?: number;
  live_queued?: number;
  live_sent?: number;
  live_failed?: number;
  live_blocked_dnc?: number;
  list_name?: string | null;
}

export type KeywordAction = 'none' | 'join_list' | 'leave_list';

export interface SmsKeywordRow {
  id: string;
  reseller_id: string;
  customer_id: string | null;
  keyword: string;
  action_kind: string;
  reply_body: string | null;
  list_id: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  list_name?: string | null;
}

export const smsCampaigns = {
  listBroadcasts: (p: { customer_id?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (p.customer_id) q.set('customer_id', p.customer_id);
    if (p.limit) q.set('limit', String(p.limit));
    return apiFetch<{ rows: SmsBroadcastRow[] }>(`/api/sms-broadcasts?${q}`);
  },
  createBroadcast: (body: {
    list_id: string;
    body: string;
    name?: string | null;
    from_number?: string | null;
    scheduled_at?: string | null;
    customer_id?: string;
  }) =>
    apiFetch<SmsBroadcastRow>('/api/sms-broadcasts', {
      method: 'POST', body: JSON.stringify(body),
    }),
  cancelBroadcast: (id: string) =>
    apiFetch<SmsBroadcastRow>(`/api/sms-broadcasts/${id}/cancel`, { method: 'POST' }),

  listKeywords: (p: { customer_id?: string } = {}) => {
    const q = new URLSearchParams();
    if (p.customer_id) q.set('customer_id', p.customer_id);
    return apiFetch<{ rows: SmsKeywordRow[] }>(`/api/sms-keywords?${q}`);
  },
  createKeyword: (body: {
    keyword: string;
    action: KeywordAction;
    list_id?: string | null;
    reply_body?: string | null;
    customer_id?: string;
  }) =>
    apiFetch<SmsKeywordRow>('/api/sms-keywords', {
      method: 'POST', body: JSON.stringify(body),
    }),
  updateKeyword: (id: string, body: {
    action?: KeywordAction;
    list_id?: string | null;
    reply_body?: string | null;
    active?: boolean;
  }) =>
    apiFetch<SmsKeywordRow>(`/api/sms-keywords/${id}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  removeKeyword: (id: string) =>
    apiFetch<void>(`/api/sms-keywords/${id}`, { method: 'DELETE' }),
};
